import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IArticleLocale,
  IArticle,
  IArticleCategoryLocale,
  IArticleCategory,
  IArticlePaginationResult,
  IArticleListResult,
  IArticleCategoryListResult,
  IArticleCategoryPaginationResult,
  IArticleRelation,
  IArticleRelationPaginationResult,
} from '../interfaces/Article.js';
import { ImageHierarchical } from './Image.js';
import { PaginationResult, ListResult } from './List.js';
import { Author } from './User.js';

/**
 * ArticleCategoryLocale
 */
class ArticleCategoryLocale extends ABaseModel implements IArticleCategoryLocale {
  id: number;
  localeCode: string;
  name: string;
  uri: string;
  description: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleCategory
 */
class ArticleCategory extends ABaseModel implements IArticleCategory {
  id: number;
  type: string;
  name: string;
  uri: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  pageTitle: string;
  locales: Array<ArticleCategoryLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleLocale
 */
class ArticleLocale extends ABaseModel implements IArticleLocale {
  id: number;
  localeCode: string;
  slug: string;
  actionText: string;
  actionLink: string;
  title: string;
  summary: string;
  text: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * Article
 */
class Article extends ABaseModel implements IArticle {
  actionText: string;
  actionLink: string;
  author: Author;
  categories: Array<ArticleCategory>;
  id: number;
  images: Array<ImageHierarchical>;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  pageTitle: string;
  publishedDate: string;
  showcase: boolean;
  slug: string;
  status: number;
  summary: string;
  tags: Array<string>;
  text: string;
  title: string;
  type: string;
  locales: Array<ArticleLocale>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleListResult
 */
class ArticleListResult extends ListResult implements IArticleListResult {
  declare items: Array<Article>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticlePaginationResult
 */
class ArticlePaginationResult extends PaginationResult implements IArticlePaginationResult {
  declare items: Array<Article>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleCategoryListResult
 */
class ArticleCategoryListResult extends ListResult implements IArticleCategoryListResult {
  declare items: Array<ArticleCategory>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleCategoryPaginationResult
 */
class ArticleCategoryPaginationResult extends PaginationResult implements IArticleCategoryPaginationResult {
  declare items: Array<ArticleCategory>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleRelation
 */
class ArticleRelation extends ABaseModel implements IArticleRelation {
  id: number;
  articleId: number;
  relationType: string;
  relationId: number;
  relationName: string;
  relationSlug: string;
  showInRelation: boolean;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArticleRelationPaginationResult
 */
class ArticleRelationPaginationResult extends PaginationResult implements IArticleRelationPaginationResult {
  declare items: Array<ArticleRelation>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

export {
  ArticleCategoryLocale,
  ArticleCategory,
  ArticleLocale,
  Article,
  ArticlePaginationResult,
  ArticleListResult,
  ArticleCategoryListResult,
  ArticleCategoryPaginationResult,
  ArticleRelation,
  ArticleRelationPaginationResult,
};
